import { EditOutlined } from "@ant-design/icons";
import { AgGridReact } from "ag-grid-react";
import { Button, Divider } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import CollapsibleSidebarLayout from "components/collapsible-sidebar-layout/collapsible-sidebar-layout";
import ErrorBoundary from "components/error-boundary";
import ConditionalFormattingToolPanel from "components/conditional-formatting";
import { getConditionalFormattingHeaderMenuItems } from "components/conditional-formatting/conditional-formatting-header-menu";
import { useConditionalFormatting } from "components/conditional-formatting/use-conditional-formatting";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import {
  ColDef,
  ColGroupDef,
  ColumnMovedEvent,
  ColumnVisibleEvent
} from "ag-grid-enterprise";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { excelStyles } from "components/excel-ag-grid-export/excel-style";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import {
  calculateCustomReportStats,
  getGridStatusBudge
} from "components/ciq-log-page-header/grid-status-budge";
import { useLocationBreakdownV2Structure } from "hooks/location-breakdown-v2-structure";
import {
  ProjectPermissionEnum,
  SubmittalStatusToStrMap
} from "constants/index";
import { FilterItem } from "models/types";
import { GridLoadingIndicator } from "components/widgets";
import { FeatureTypes } from "entity-app/constants";
import { useStatusMilestoneNameOptions } from "hooks/use-status-milestone-name-options";
import { useEnumValues } from "hooks/enum-values";
import RenameColumnsModal from "./rename-columns-modal";
import {
  useCustomReports,
  useCustomReportsDispatch
} from "../context/custom-reports-provider";
import {
  applyUnassignedDefaults,
  flattenData,
  flattenJSON1ToJSON2,
  reorderJSON1FromJSON2,
  translateVLargeMaterial,
  updateColumnVisibility,
  getSidebarColumnLayoutForCustomReportDateBlock,
  resolveDateBlockVisibilityForGridCol,
  canonicalDateBlockFieldKey,
  dedupeDateBlockFieldsInColumnConfig
} from "../utils/utils";
import useReportColumnDef from "../hooks/report-column-def";
import DatasetConfigTags from "./dataset-config-tags";
import {
  CommentsSidebarContent,
  CommentsSidebarHeader
} from "./comments-sidebar";
import { useCommentsCountLabel } from "components/feature-activity-card/use-comments-count-label";
import { getCustomReportData } from "../services/custom-report-services";
import { RiskLevelType } from "components/date-block/models";

const findDateBlockFieldIndex = (fields: any[], canonLeaf: string): number =>
  fields.findIndex(
    (field: any) => canonicalDateBlockFieldKey(field.field_key) === canonLeaf
  );

/**
 * Real (non date-block) columns whose ids collide with the date-block prefixes.
 * `milestone_state` is the material Status column, not a date-block leaf.
 */
const NON_DATE_BLOCK_COLUMN_IDS = new Set(["milestone_state"]);

/** Parent keys whose grid toggle cascades to every child column. */
const DATE_BLOCK_PARENT_KEYS = new Set(["milestones", "offsets"]);

const hasDateBlockFieldKey = (config: any[], key: string): boolean => {
  const want = canonicalDateBlockFieldKey(key);
  return config.some((col: any) => {
    if (
      typeof col.field_key === "string" &&
      canonicalDateBlockFieldKey(col.field_key) === want
    )
      return true;
    if (Array.isArray(col.fields)) return hasDateBlockFieldKey(col.fields, key);
    return false;
  });
};

const ensureDateBlockParent = (config: any[], parentKey: string): any[] => {
  const hasTopLevelParent = config.some(
    (col: any) => col.field_key === parentKey
  );
  if (hasTopLevelParent) return config;
  return [
    ...config,
    {
      field_key: parentKey,
      caption_en: parentKey === "milestones" ? "Milestones" : "Durations",
      original_caption_en:
        parentKey === "milestones" ? "Milestones" : "Durations",
      order: config.length + 1,
      allow_rename: false,
      field_type: "object",
      visibility: true,
      customizable: false,
      fields: []
    }
  ];
};

const upsertDateBlockLeaf = (
  config: any[],
  parentKey: string,
  leaf: any
): any[] => {
  const canonLeaf = canonicalDateBlockFieldKey(leaf.field_key);
  const normalizedLeaf = { ...leaf, field_key: canonLeaf };
  const base = ensureDateBlockParent(config, parentKey);
  return base.map((col: any) => {
    if (col.field_key !== parentKey) return col;
    const fields = Array.isArray(col.fields) ? [...col.fields] : [];
    const existingIdx = findDateBlockFieldIndex(fields, canonLeaf);
    if (existingIdx === -1) {
      fields.push({
        ...normalizedLeaf,
        order: fields.length + 1
      });
    } else {
      fields[existingIdx] = {
        ...fields[existingIdx],
        ...normalizedLeaf,
        order: fields[existingIdx].order
      };
    }
    return { ...col, fields };
  });
};

const dateBlockLeafType = (fieldKey: string) => {
  // Legacy keys: milestone_*_date_#, offset_duration_#
  if (fieldKey.includes("_date_")) return "date";
  if (fieldKey.includes("_duration_")) return "number";

  // New keys: milestones__*, offsets__* (and legacy unprefixed)
  if (/_offset_\d+_duration$/.test(fieldKey)) return "number";
  if (
    fieldKey.includes("planned_milestone_") ||
    fieldKey.includes("projected_milestone_") ||
    fieldKey.includes("actual_milestone_")
  ) {
    return "date";
  }
  if (fieldKey.startsWith("offset_")) return "number";
  return "string";
};

/** Folds one grid column's visibility change into the saved columns_config. */
const applyDateBlockColumnToConfig = (
  config: any[],
  fieldKey: string,
  headerText: string,
  isVisible: boolean
): any[] => {
  let nextConfig = config;

  if (
    DATE_BLOCK_PARENT_KEYS.has(fieldKey) &&
    !hasDateBlockFieldKey(nextConfig, fieldKey)
  ) {
    nextConfig = ensureDateBlockParent(nextConfig, fieldKey);
  }

  // Real columns (e.g. milestone_state) share the date-block prefixes but
  // must never be written into columns_config as date-block leaves.
  const isDateBlockLeafCandidate = !NON_DATE_BLOCK_COLUMN_IDS.has(fieldKey);
  const isMilestoneLeafKey =
    isDateBlockLeafCandidate && fieldKey.includes("milestone_");
  const isOffsetLeafKey =
    isDateBlockLeafCandidate && fieldKey.includes("offset_");

  if (
    (isMilestoneLeafKey || isOffsetLeafKey) &&
    !hasDateBlockFieldKey(nextConfig, fieldKey)
  ) {
    nextConfig = upsertDateBlockLeaf(
      nextConfig,
      isMilestoneLeafKey ? "milestones" : "offsets",
      {
        field_key: fieldKey,
        caption_en: headerText,
        original_caption_en: headerText,
        allow_rename: false,
        field_type: dateBlockLeafType(fieldKey),
        visibility: true,
        customizable: false
      }
    );
  }

  return updateColumnVisibility(nextConfig, fieldKey, isVisible);
};

const columnsFromVisibleEvent = (e: ColumnVisibleEvent) => {
  if (e.columns?.length) return e.columns;
  return e.column ? [e.column] : [];
};

/**
 * True for the date-block group/leaf columns the sidebar tree layout owns.
 * These are stripped from the existing layout before the generated tree is
 * appended; anything else must be kept or it disappears from the tool panel.
 */
const isDateBlockLayoutColId = (colId: string): boolean => {
  if (NON_DATE_BLOCK_COLUMN_IDS.has(colId)) return false;
  if (colId === "milestones" || colId === "offsets") return true;
  // Date-block dynamic cols only (do not use includes("milestone_"): strips planned_milestone_1, etc.)
  if (colId.startsWith("milestones__") || colId.startsWith("offsets__"))
    return true;
  if (colId.startsWith("milestone_")) return true; // legacy date-block
  if (colId.startsWith("offset_")) return true; // legacy e.g. offset_1_duration
  return false;
};

type Props = {
  sidebarState: any[];
  onUpdateColumnConfig: (config: any) => Promise<void>;
  onUpdateProjectReport: (data: any) => Promise<void>;
  projectId: string;
  reportId: string;
  gridRef: React.RefObject<AgGridReact<any>>;
};

const columnsToIgnore = ["quantity_unit_id", "material_size_unit"];

/** Union ids so layout can collapse groups in getState() while we still keep pre-layout expansion. */
const mergeExpandedGroupIds = (
  a: string[] | undefined,
  b: string[] | undefined
): string[] | undefined => {
  const merged = [...new Set([...(a ?? []), ...(b ?? [])])];
  return merged.length ? merged : undefined;
};

const applyColumnVisibilityForDateblockColumns = ({
  columnConfig,
  dateBlockColumns,
  gridRef
}: {
  columnConfig: any;
  dateBlockColumns: any;
  gridRef: any;
}) => {
  if (!columnConfig || !dateBlockColumns) return;
  const { dateBlockColumnIds } = dateBlockColumns;
  const { milestones, offsets } = dateBlockColumnIds;

  const milestonesColumnConfig = columnConfig.find(
    (col: any) => col.field_key === "milestones"
  );
  const offsetsColumnConfig = columnConfig.find(
    (col: any) => col.field_key === "offsets"
  );

  const buildVisibilityMap = (
    config: any[],
    map = new Map<string, boolean>()
  ) => {
    config.forEach((col: any) => {
      if (typeof col?.field_key === "string") {
        map.set(canonicalDateBlockFieldKey(col.field_key), !!col.visibility);
      }
      if (Array.isArray(col?.fields)) {
        buildVisibilityMap(col.fields, map);
      }
    });
    return map;
  };

  const visibilityByKey = buildVisibilityMap(columnConfig);

  if (milestonesColumnConfig) {
    gridRef.current?.api.setColumnsVisible(
      milestones,
      milestonesColumnConfig.visibility
    );
  }
  if (offsetsColumnConfig) {
    gridRef.current?.api.setColumnsVisible(
      offsets,
      offsetsColumnConfig.visibility
    );
  }

  // Apply per-leaf visibility overrides (if saved) after parent visibility,
  // so user-selected leaf toggles persist across reload.
  milestones.forEach((colId: string) => {
    const vis = resolveDateBlockVisibilityForGridCol(colId, visibilityByKey);
    if (vis === undefined) return;
    gridRef.current?.api.setColumnsVisible([colId], vis);
  });
  offsets.forEach((colId: string) => {
    const vis = resolveDateBlockVisibilityForGridCol(colId, visibilityByKey);
    if (vis === undefined) return;
    gridRef.current?.api.setColumnsVisible([colId], vis);
  });
};

function ResultsPanel(props: Props) {
  const {
    sidebarState,
    onUpdateColumnConfig,
    onUpdateProjectReport,
    projectId,
    reportId,
    gridRef
  } = props;
  const {
    projectDetails,
    tokenContents,
    projectToken,
    isFeatureFlagEnabled,
    columnHeaders
  } = useContext(ProjectContext);
  const materialCustomColumns = columnHeaders?.materialCustomColumns;
  const { enumOptionsMap } = useEnumValues(
    FeatureTypes.MATERIALS,
    !materialCustomColumns?.length
  );
  const {
    datasetConfig,
    gridState,
    columnConfig,
    permissions,
    conditionalsFormatting
  } = useCustomReports();
  const dispatch: any = useCustomReportsDispatch();
  const [isSidebarOpen, setIsSidebarOpen] = sidebarState;
  const filterChipRef = useRef<FilterChipComponentRef>(null);
  const columnConfigRef = useRef<any[] | null>(null);
  const expandedToolPanelGroupIdsRef = useRef<string[] | undefined>(undefined);
  const pendingExpandedGroupRestoreTimeoutsRef = useRef<
    Array<ReturnType<typeof setTimeout>>
  >([]);
  const [isGridReady, setIsGridReady] = useState(false);
  const [isOpenRenameColumnsModal, setIsOpenRenameColumnsModal] =
    useState(false);
  const [gridFilterItems, setGridFilterItems] = useState<FilterItem[]>([]);
  const [isFirstDataRendered, setFirstDataRendered] = useState(false);

  const [rowGroupExpandedState, setRowGroupExpandedState] = useState<{
    isAllExpanded: boolean;
    isAllCollapsed: boolean;
  }>({
    isAllExpanded: false,
    isAllCollapsed: true
  });

  const [vLargeMaterial, setVLargeMaterial] = useState<any[] | null>(null);
  const [isVLargeMaterialLoading, setIsVLargeMaterialLoading] = useState(false);

  const getReportData = useCallback(async () => {
    if (!reportId || !projectToken) return;

    const response = await getCustomReportData(reportId, projectToken);
    setVLargeMaterial(response?.data || null);
    setIsVLargeMaterialLoading(false);
  }, [reportId, projectToken]);

  useEffect(() => {
    getReportData();
  }, [getReportData]);

  const materials = useMemo(() => {
    if (!vLargeMaterial) return null;
    return translateVLargeMaterial(vLargeMaterial);
  }, [vLargeMaterial]);

  const rowData = useMemo(() => {
    if (!datasetConfig?.linkedEntity || !materials || isVLargeMaterialLoading)
      return null;

    let opMaterials = [...materials];

    const inlineModeForFeatures = datasetConfig?.linkedEntity
      .filter((entity) => entity.display_type === "inline")
      .reduce((acc, entity) => {
        acc.push(entity.feature_type_id);
        return acc;
      }, [] as number[]);

    opMaterials = applyUnassignedDefaults(
      opMaterials,
      datasetConfig?.groupByField,
      columnConfig
    );

    opMaterials = flattenData(opMaterials, inlineModeForFeatures);

    return opMaterials;
  }, [
    columnConfig,
    datasetConfig?.groupByField,
    datasetConfig?.linkedEntity,
    isVLargeMaterialLoading,
    materials
  ]);

  const [commentsMaterialId, setCommentsMaterialId] = useState<string | null>(
    null
  );

  const commentsMaterial = useMemo(() => {
    if (!commentsMaterialId) return null;
    return materials?.find((m: any) => m.id === commentsMaterialId) ?? null;
  }, [commentsMaterialId, materials]);

  const isCommentsSidebarOpen = !!commentsMaterial;

  const { label: commentsSidebarTitle, onCommentsCountChange } =
    useCommentsCountLabel(commentsMaterial?.id);

  const rowDataRef = useRef(rowData);
  rowDataRef.current = rowData;

  const onCommentClick = useCallback(
    ({ sourceRowIndex }: { sourceRowIndex: number | undefined | null }) => {
      if (sourceRowIndex === undefined || sourceRowIndex === null) return;
      const row = rowDataRef.current?.[sourceRowIndex];
      if (!row?.id) return;
      setCommentsMaterialId(row.id);
    },
    []
  );

  const closeCommentsSidebar = useCallback(() => {
    setCommentsMaterialId(null);
  }, []);

  useEffect(() => {
    if (!isCommentsSidebarOpen) return;
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeCommentsSidebar();
    };
    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isCommentsSidebarOpen, closeCommentsSidebar]);

  const rowClassRules = useMemo(
    () => ({
      "selected-item-for-panel": (params: any) => {
        if (params.node?.group) return false;
        return !!commentsMaterialId && params.data?.id === commentsMaterialId;
      }
    }),
    [commentsMaterialId]
  );

  const enableCellSpan = useMemo(() => {
    const hasInlineDisplayType = datasetConfig?.linkedEntity.some(
      (entity) => entity.display_type === "inline"
    );
    return !!hasInlineDisplayType;
  }, [datasetConfig?.linkedEntity]);

  const canSetLBSForMaterial = !isPermissionNotGrantted(
    ProjectPermissionEnum.CanSetLBSForMaterial,
    tokenContents?.role ?? ""
  );
  const isMaterialCsiClassificationEnabled = isFeatureFlagEnabled(
    "MATERIAL_CSI_CLASSIFICATION"
  );

  const { isLBSFeatureEnabled, handleOnFilterOpen } =
    useLocationBreakdownV2Structure({
      entityList: rowData || [],
      projectId: projectDetails?.id ?? "",
      gridRef,
      hasPermissionToSetLBSForMaterial: canSetLBSForMaterial,
      locationDataKey: "project_locations"
    });

  const {
    columnDefs,
    defaultColDef,
    autoGroupColumnDef,
    sideBarConfig,
    dateBlockColumns
  } = useReportColumnDef({
    reportItems: vLargeMaterial || [],
    linkedEntityDisplayType: datasetConfig?.linkedEntity ?? [],
    groupByField: datasetConfig?.groupByField ?? "none",
    isLBSFeatureEnabled,
    isMaterialCsiClassificationEnabled,
    projectId,
    materialCustomColumns,
    enumOptionsMap
  });
  const {
    conditionalFormattingRules,
    conditionalFormattingDefaultColDef,
    conditionalFormattingExcelStyles,
    setConditionalFormattingRules
  } = useConditionalFormatting({
    gridRef,
    defaultColDef
  });
  const statusMilestoneOptions = useStatusMilestoneNameOptions(
    FeatureTypes.MATERIALS
  );
  const mergedExcelStyles = useMemo(
    () => [...excelStyles, ...conditionalFormattingExcelStyles],
    [conditionalFormattingExcelStyles]
  );

  // Apply persisted CF rules from report query_configs onto the grid.
  useEffect(() => {
    if (conditionalsFormatting == null) return;
    setConditionalFormattingRules(conditionalsFormatting);
  }, [conditionalsFormatting, setConditionalFormattingRules]);

  const onConditionalFormattingRulesChange = useCallback(
    async (rules: typeof conditionalFormattingRules) => {
      setConditionalFormattingRules(rules);
      dispatch({
        type: "UPDATE_CONDITIONALS_FORMATTING",
        payload: rules
      });
      await onUpdateProjectReport({ conditionals_formatting: rules });
    },
    [dispatch, onUpdateProjectReport, setConditionalFormattingRules]
  );

  useEffect(() => {
    columnConfigRef.current = columnConfig || null;
  }, [columnConfig]);

  const getColumnsToolPanel = useCallback(() => {
    if (typeof gridRef.current?.api?.getToolPanelInstance !== "function") {
      return null;
    }
    return gridRef.current.api.getToolPanelInstance?.("columns");
  }, [gridRef]);

  const applyToolPanelStatePreservingExpandedGroups = useCallback(
    (columnsToolPanel: any, stateBeforeLayout?: any) => {
      const stateAfterLayout = (columnsToolPanel as any)?.getState?.();

      const expandedBefore =
        Array.isArray(stateBeforeLayout?.expandedGroupIds) &&
        stateBeforeLayout.expandedGroupIds.length > 0
          ? stateBeforeLayout.expandedGroupIds
          : undefined;
      const expandedAfter =
        Array.isArray(stateAfterLayout?.expandedGroupIds) &&
        stateAfterLayout.expandedGroupIds.length > 0
          ? stateAfterLayout.expandedGroupIds
          : undefined;

      const hasLayoutSnapshot = stateBeforeLayout !== undefined;
      let expandedToApply: string[] | undefined;
      if (hasLayoutSnapshot) {
        expandedToApply =
          mergeExpandedGroupIds(expandedBefore, expandedAfter) ??
          expandedToolPanelGroupIdsRef.current;
      } else {
        expandedToApply =
          expandedAfter ??
          expandedBefore ??
          expandedToolPanelGroupIdsRef.current;
      }

      if (expandedToApply) {
        expandedToolPanelGroupIdsRef.current = [...expandedToApply];
      }

      if (expandedToApply?.length) {
        // Avoid setting full tool-panel state, which can reset panel UI.
        // Explicitly re-open the previously expanded groups.
        (columnsToolPanel as any)?.expandColumnGroups?.([...expandedToApply]);
      }
    },
    []
  );

  const captureExpandedToolPanelGroups = useCallback(() => {
    const columnsToolPanel = getColumnsToolPanel();
    const toolPanelState = (columnsToolPanel as any)?.getState?.();
    if (
      Array.isArray(toolPanelState?.expandedGroupIds) &&
      toolPanelState.expandedGroupIds.length > 0
    ) {
      expandedToolPanelGroupIdsRef.current = [
        ...toolPanelState.expandedGroupIds
      ];
    }
  }, [getColumnsToolPanel]);

  const restoreExpandedToolPanelGroups = useCallback(() => {
    const columnsToolPanel = getColumnsToolPanel();
    if (!columnsToolPanel) return;
    applyToolPanelStatePreservingExpandedGroups(columnsToolPanel);
  }, [applyToolPanelStatePreservingExpandedGroups, getColumnsToolPanel]);

  const scheduleRestoreExpandedToolPanelGroups = useCallback(() => {
    pendingExpandedGroupRestoreTimeoutsRef.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    pendingExpandedGroupRestoreTimeoutsRef.current = [0, 50, 150, 400].map(
      (delayMs) =>
        setTimeout(() => {
          restoreExpandedToolPanelGroups();
        }, delayMs)
    );
  }, [restoreExpandedToolPanelGroups]);

  const applySidebarTreeLayout = useCallback(() => {
    if (!gridRef.current?.api || !dateBlockColumns) return;
    const columnGroupState = gridRef.current.api.getColumnGroupState?.();
    const { sidebarColumnLayout } =
      getSidebarColumnLayoutForCustomReportDateBlock(dateBlockColumns);
    if (typeof gridRef.current.api.getToolPanelInstance !== "function") return;
    const columnsToolPanel =
      gridRef.current.api.getToolPanelInstance?.("columns");
    if (!columnsToolPanel) return;
    const toolPanelStateBeforeLayout = (columnsToolPanel as any).getState?.();
    const existingLayout =
      (gridRef.current.api.getColumnDefs() as (ColDef | ColGroupDef)[]) || [];
    const filteredExistingLayout = existingLayout.filter(
      (item: ColDef | ColGroupDef) => {
        const colId = (item as ColDef).colId;
        if (!colId) return true;
        return !isDateBlockLayoutColId(colId);
      }
    );
    columnsToolPanel.setColumnLayout([
      ...filteredExistingLayout,
      ...sidebarColumnLayout
    ]);
    applyToolPanelStatePreservingExpandedGroups(
      columnsToolPanel,
      toolPanelStateBeforeLayout
    );
    if (columnGroupState) {
      gridRef.current.api.setColumnGroupState?.(columnGroupState);
    }
  }, [applyToolPanelStatePreservingExpandedGroups, dateBlockColumns, gridRef]);

  const applyColumnStateFromConfig = useCallback(
    (gridApi: any) => {
      // Tool-panel edits land in the ref before the context (which updates
      // only after the save round-trip), so the ref is the latest config.
      const latestColumnConfig = columnConfigRef.current || columnConfig;
      if (!latestColumnConfig || !gridApi) return;

      const flattened = flattenJSON1ToJSON2(
        latestColumnConfig,
        datasetConfig?.linkedEntity[0]?.display_type
      );

      const columnState = flattened.map((col: any) => {
        const fieldSorting: any = gridState?.sortState?.find(
          (sort: any) => sort.field_key === col.colId
        );

        let { hide } = col;
        const linkedSubmittalColConfig = datasetConfig?.linkedEntity.find(
          (entity) =>
            entity.feature_type_id === (FeatureTypes.SUBMITTALS as number)
        );

        const linkedActivityColConfig = datasetConfig?.linkedEntity.find(
          (entity) =>
            entity.feature_type_id === (FeatureTypes.ACTIVITIES as number)
        );

        if (col.colId === "linked_submittals") {
          hide = linkedSubmittalColConfig?.display_type === "none";
        }

        if (col.colId === "linked_activities") {
          hide = linkedActivityColConfig?.display_type === "none";
        }

        if (
          datasetConfig?.groupByField !== "none" &&
          col.colId === datasetConfig?.groupByField
        ) {
          hide = true;
        }

        const config: any = {
          colId:
            col.colId === "group_by_column" ? "ag-Grid-AutoColumn" : col.colId,
          hide,
          order: col.order ?? 999 // ordering (lower comes first)
        };

        if (fieldSorting) {
          const fieldSortIndex = gridState?.sortState?.findIndex(
            (sort: any) => sort.field_key === col.colId
          );
          config.sort = fieldSorting.sort;
          config.sortIndex = fieldSortIndex;
        }

        return config;
      });

      // Sort by order for applying
      const cleanedColumnState = columnState
        .sort((a, b) => a.order - b.order)
        .filter((col) => !columnsToIgnore.includes(col.colId));

      gridApi.applyColumnState({
        state: cleanedColumnState,
        applyOrder: true
      });
    },
    [
      columnConfig,
      datasetConfig?.groupByField,
      datasetConfig?.linkedEntity,
      gridState?.sortState
    ]
  );

  const onFirstDataRendered = () => {
    gridRef.current?.api.setFilterModel(gridState?.filterModel || null);

    applyColumnVisibilityForDateblockColumns({
      dateBlockColumns,
      columnConfig,
      gridRef
    });

    applyColumnStateFromConfig(gridRef.current?.api);

    const applyTreeLayoutToToolPanel = () => {
      if (typeof gridRef.current?.api?.getToolPanelInstance !== "function")
        return;
      const columnsToolPanel =
        gridRef.current?.api?.getToolPanelInstance?.("columns");
      if (!columnsToolPanel || !gridRef.current?.api) return;
      const toolPanelStateBeforeLayout = (columnsToolPanel as any).getState?.();

      const { sidebarColumnLayout } =
        getSidebarColumnLayoutForCustomReportDateBlock(dateBlockColumns);

      const existingLayout =
        (gridRef.current.api.getColumnDefs() as (ColDef | ColGroupDef)[]) || [];

      const filteredExistingLayout = existingLayout.filter(
        (item: ColDef | ColGroupDef) => {
          const colId = (item as ColDef).colId;
          if (!colId) return true;
          return !isDateBlockLayoutColId(colId);
        }
      );

      columnsToolPanel.setColumnLayout([
        ...filteredExistingLayout,
        ...sidebarColumnLayout
      ]);
      applyToolPanelStatePreservingExpandedGroups(
        columnsToolPanel,
        toolPanelStateBeforeLayout
      );
    };

    // Apply tree layout after column state/visibility is applied
    setTimeout(applyTreeLayoutToToolPanel, 0);

    setFirstDataRendered(true);
  };

  const saveFilterChange = useCallback(async () => {
    const filterModel = gridRef.current?.api.getFilterModel();

    const calcStats = calculateCustomReportStats({
      gridRef
    });
    dispatch({
      type: "UPDATE_GRID_STATS_BADGE",
      payload: getGridStatusBudge({
        title: "Report",
        totalCount: materials?.length,
        filterStats: calcStats
      })
    });
    // return;
    await onUpdateProjectReport({ filters_config: filterModel });
  }, [dispatch, materials?.length, onUpdateProjectReport, gridRef]);

  const onFilterChanged = useCallback(
    async (e: any) => {
      filterChipRef.current?.onFilterApplied();
      if (e.source === "api") return;

      dispatch({
        type: "UPDATE_GRID_STATE",
        payload: {
          filterModel: e.api.getFilterModel()
        }
      });

      saveFilterChange();
    },
    [dispatch, saveFilterChange]
  );

  const withAllDateBlockColumns = useCallback(
    (config: any[]) => {
      if (!Array.isArray(config) || !dateBlockColumns?.dateBlockColumnIds) {
        return config;
      }

      const hasFieldKey = (columns: any[], key: string): boolean => {
        const want = canonicalDateBlockFieldKey(key);
        return columns.some((col: any) => {
          if (
            typeof col.field_key === "string" &&
            canonicalDateBlockFieldKey(col.field_key) === want
          )
            return true;
          if (Array.isArray(col.fields)) return hasFieldKey(col.fields, key);
          return false;
        });
      };

      const ensureParentExists = (columns: any[], parentKey: string): any[] => {
        const hasParent = columns.some(
          (col: any) => col.field_key === parentKey
        );
        if (hasParent) return columns;
        return [
          ...columns,
          {
            field_key: parentKey,
            caption_en: parentKey === "milestones" ? "Milestones" : "Durations",
            original_caption_en:
              parentKey === "milestones" ? "Milestones" : "Durations",
            order: columns.length + 1,
            allow_rename: false,
            field_type: "object",
            visibility: true,
            customizable: false,
            fields: []
          }
        ];
      };

      const getLeafType = (fieldKey: string): string => {
        if (fieldKey.includes("_date_")) return "date";
        if (fieldKey.includes("_duration_")) return "number";
        // Dead branch: any key that startsWith "offset_duration_" necessarily
        // contains "_duration_", so the check above always returns first.
        /* v8 ignore next */
        if (fieldKey.startsWith("offset_duration_")) return "number";
        if (/_offset_\d+_duration$/.test(fieldKey)) return "number";
        if (
          fieldKey.includes("planned_milestone_") ||
          fieldKey.includes("projected_milestone_") ||
          fieldKey.includes("actual_milestone_")
        ) {
          return "date";
        }
        return "string";
      };

      const getHeaderText = (fieldKey: string): string => {
        return (
          gridRef.current?.api
            ?.getColumnDef?.(fieldKey as any)
            ?.headerName?.toString?.() || fieldKey
        );
      };

      const upsertUnderParent = (
        columns: any[],
        parentKey: string,
        leafFieldKey: string
      ): any[] => {
        const canonLeaf = canonicalDateBlockFieldKey(leafFieldKey);
        const base = ensureParentExists(columns, parentKey);
        return base.map((col: any) => {
          if (col.field_key !== parentKey) return col;
          const fields = Array.isArray(col.fields) ? [...col.fields] : [];
          const existingIdx = findDateBlockFieldIndex(fields, canonLeaf);
          if (existingIdx === -1) {
            const headerText = getHeaderText(canonLeaf);
            fields.push({
              field_key: canonLeaf,
              caption_en: headerText,
              original_caption_en: headerText,
              allow_rename: false,
              field_type: getLeafType(canonLeaf),
              visibility: true,
              customizable: false,
              order: fields.length + 1
            });
          } else {
            fields[existingIdx] = {
              ...fields[existingIdx],
              field_key: canonLeaf
            };
          }
          return { ...col, fields };
        });
      };

      let nextConfig = [...config];
      const { milestones = [], offsets = [] } =
        dateBlockColumns.dateBlockColumnIds;

      nextConfig = ensureParentExists(nextConfig, "milestones");
      nextConfig = ensureParentExists(nextConfig, "offsets");

      milestones.forEach((fieldKey: string) => {
        if (!hasFieldKey(nextConfig, fieldKey)) {
          nextConfig = upsertUnderParent(nextConfig, "milestones", fieldKey);
        }
      });
      offsets.forEach((fieldKey: string) => {
        if (!hasFieldKey(nextConfig, fieldKey)) {
          nextConfig = upsertUnderParent(nextConfig, "offsets", fieldKey);
        }
      });

      return dedupeDateBlockFieldsInColumnConfig(nextConfig);
    },
    [dateBlockColumns, gridRef]
  );

  const onColumnVisible = useCallback(
    async (e: ColumnVisibleEvent) => {
      if (e.source === "api") return;
      captureExpandedToolPanelGroups();

      const cols = columnsFromVisibleEvent(e);
      if (!cols.length || !columnConfig || !isFirstDataRendered) return;

      // Dead fallback: the columnConfig effect always sets columnConfigRef.current to
      // the (truthy) columnConfig before any handler can run, so the right operand is
      // unreachable.
      /* v8 ignore next */
      let nextConfig = (columnConfigRef.current || columnConfig) as any[];
      for (const col of cols) {
        const fieldKey = canonicalDateBlockFieldKey(col.getColId());
        const isVisible = col.isVisible();
        const headerText = col.getColDef?.()?.headerName || fieldKey;

        nextConfig = applyDateBlockColumnToConfig(
          nextConfig,
          fieldKey,
          headerText,
          isVisible
        );

        // Keep existing behavior for parent toggles: toggle all child columns in grid.
        if (DATE_BLOCK_PARENT_KEYS.has(fieldKey)) {
          gridRef?.current?.api?.setColumnsVisible(
            (dateBlockColumns.dateBlockColumnIds as any)[fieldKey],
            isVisible
          );
        }
      }

      nextConfig = withAllDateBlockColumns(nextConfig);
      columnConfigRef.current = nextConfig;

      if (e.source !== "toolPanelUi") {
        dispatch({
          type: "UPDATE_COLUMN_CONFIG",
          payload: nextConfig
        });
      }
      scheduleRestoreExpandedToolPanelGroups();
      try {
        await onUpdateProjectReport({ columns_config: nextConfig });
        if (e.source === "toolPanelUi") {
          dispatch({
            type: "UPDATE_COLUMN_CONFIG",
            payload: nextConfig
          });
        }
      } finally {
        scheduleRestoreExpandedToolPanelGroups();
      }
    },
    [
      captureExpandedToolPanelGroups,
      columnConfig,
      dateBlockColumns.dateBlockColumnIds,
      dispatch,
      gridRef,
      isFirstDataRendered,
      onUpdateProjectReport,
      scheduleRestoreExpandedToolPanelGroups,
      withAllDateBlockColumns
    ]
  );

  const onColumnMoved = useCallback(
    async (e: ColumnMovedEvent) => {
      if (!columnConfig || !isFirstDataRendered || e.source === "api") return;
      captureExpandedToolPanelGroups();
      const allCols = e.api.getAllGridColumns();
      const orderedKeys = allCols.map((c) => c.getColId());

      const updatedOrderedKeys = orderedKeys.map((key) =>
        key === "ag-Grid-AutoColumn" ? "group_by_column" : key
      );

      const updated = reorderJSON1FromJSON2(
        // Dead fallback: the columnConfig effect always sets columnConfigRef.current to
        // the (truthy) columnConfig before any handler can run, so the right operand is
        // unreachable.
        /* v8 ignore next */
        (columnConfigRef.current || columnConfig) as any[],
        updatedOrderedKeys,
        datasetConfig?.linkedEntity[0]?.display_type
      );
      const normalizedUpdated = withAllDateBlockColumns(updated);
      columnConfigRef.current = normalizedUpdated;
      if (e.source !== "toolPanelUi") {
        dispatch({
          type: "UPDATE_COLUMN_CONFIG",
          payload: normalizedUpdated
        });
      }
      scheduleRestoreExpandedToolPanelGroups();

      try {
        await onUpdateProjectReport({ columns_config: normalizedUpdated });
        if (e.source === "toolPanelUi") {
          dispatch({
            type: "UPDATE_COLUMN_CONFIG",
            payload: normalizedUpdated
          });
        }
      } finally {
        scheduleRestoreExpandedToolPanelGroups();
      }
    },
    [
      captureExpandedToolPanelGroups,
      columnConfig,
      datasetConfig?.linkedEntity,
      dispatch,
      isFirstDataRendered,
      onUpdateProjectReport,
      scheduleRestoreExpandedToolPanelGroups,
      withAllDateBlockColumns
    ]
  );

  const onColumnSorted = useCallback(
    async (e: any) => {
      if (e.source === "api") return;

      const colState = e.api.getColumnState();
      const sortedColumns = colState.filter((col: any) => col.sort);
      sortedColumns.sort((a: any, b: any) => a.sortIndex - b.sortIndex);
      const sortConfig = sortedColumns.map((col: any) => ({
        field_key:
          col.colId === "ag-Grid-AutoColumn" ? "group_by_column" : col.colId,
        sort: col.sort
      }));

      dispatch({
        type: "UPDATE_GRID_STATE",
        payload: {
          sortState: sortConfig
        }
      });
      // return;
      await onUpdateProjectReport({ sorts_config: sortConfig });
    },
    [dispatch, onUpdateProjectReport]
  );

  const onGridFilterOpened = useCallback(
    (event: any) => {
      if (event.column.colId === "project_locations") {
        handleOnFilterOpen(event);
      }
    },
    [handleOnFilterOpen]
  );

  const onGridReady = useCallback(
    (e: any) => {
      setIsGridReady(true);
      applyColumnStateFromConfig(e.api);
    },
    [applyColumnStateFromConfig]
  );

  // Updating columnDefs re-applies each def's own `hide`/`width`, and columns
  // added after first render (e.g. custom fields) have no saved state yet, so
  // restore the config after every column rebuild.
  const onGridColumnsChanged = useCallback(
    (e: any) => {
      if (!isFirstDataRendered) return;
      applyColumnStateFromConfig(e.api);
    },
    [applyColumnStateFromConfig, isFirstDataRendered]
  );

  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [enumOptionsMap, gridRef]);

  const isGridLoading = useMemo(() => {
    return !(
      columnConfig &&
      projectDetails &&
      rowData &&
      datasetConfig &&
      !isVLargeMaterialLoading &&
      isGridReady
    );
  }, [
    columnConfig,
    projectDetails,
    rowData,
    datasetConfig,
    isVLargeMaterialLoading,
    isGridReady
  ]);

  const gridKey = useMemo(() => {
    return `${datasetConfig?.linkedEntity[0]?.display_type}_${datasetConfig?.linkedEntity[1]?.display_type}_${datasetConfig?.groupByField}`;
  }, [datasetConfig?.groupByField, datasetConfig?.linkedEntity]);

  const onGridPreDestroyed = useCallback(() => {
    pendingExpandedGroupRestoreTimeoutsRef.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    pendingExpandedGroupRestoreTimeoutsRef.current = [];
    setIsGridReady(false);
    setFirstDataRendered(false);
  }, [setIsGridReady, setFirstDataRendered]);

  useEffect(() => {
    if (!isFirstDataRendered) return;
    const raf = requestAnimationFrame(() => {
      scheduleRestoreExpandedToolPanelGroups();
    });
    return () => cancelAnimationFrame(raf);
  }, [
    columnConfig,
    isFirstDataRendered,
    scheduleRestoreExpandedToolPanelGroups
  ]);

  useEffect(() => {
    if (!isFirstDataRendered) return;
    const hasDateBlockCols =
      (dateBlockColumns?.dateBlockColumnIds?.milestones?.length || 0) > 0 ||
      (dateBlockColumns?.dateBlockColumnIds?.offsets?.length || 0) > 0;
    if (!hasDateBlockCols) return;

    const raf = requestAnimationFrame(() => {
      applySidebarTreeLayout();
      scheduleRestoreExpandedToolPanelGroups();
    });

    return () => cancelAnimationFrame(raf);
  }, [
    applySidebarTreeLayout,
    dateBlockColumns?.dateBlockColumnIds?.milestones,
    dateBlockColumns?.dateBlockColumnIds?.offsets,
    isFirstDataRendered,
    scheduleRestoreExpandedToolPanelGroups
  ]);

  const gridStyleConfig = useMemo(() => {
    const isAnyCondensed = datasetConfig?.linkedEntity?.some(
      (entity) => entity.display_type === "condensed"
    );
    return {
      rowHeight: isAnyCondensed ? 62 : 50
    };
  }, [datasetConfig?.linkedEntity]);

  const onExpandOrCollapseAll = useCallback((params: any) => {
    setRowGroupExpandedState({
      isAllExpanded: params.source === "expandAll",
      isAllCollapsed: params.source === "collapseAll"
    });
  }, []);

  const onRowGroupOpened = useCallback(
    (params: any) => {
      let isAllExpanded = true;
      let isAllCollapsed = true;
      params.api.forEachNode((node: any) => {
        if (node.rowGroupIndex !== undefined) {
          isAllExpanded = isAllExpanded && node.expanded;
          isAllCollapsed = isAllCollapsed && !node.expanded;
        }
      });
      setRowGroupExpandedState({
        isAllExpanded,
        isAllCollapsed
      });
    },
    [setRowGroupExpandedState]
  );

  const onExpandAll = useCallback(() => {
    gridRef.current?.api.expandAll();
  }, [gridRef]);

  const onCollapseAll = useCallback(() => {
    gridRef.current?.api.collapseAll();
  }, [gridRef]);

  useEffect(() => {
    setRowGroupExpandedState({
      isAllExpanded: false,
      isAllCollapsed: true
    });
  }, [datasetConfig?.groupByField]);

  const resultsContent = (
    <div
      className="flex flex-col w-full h-full bg-ciq-gray-50 bg-opacity-50"
      data-testid="custom-report-results-panel"
    >
      <div
        className="px-6 py-2 w-full flex space-x-2 items-center justify-between"
        data-testid="custom-report-results-header"
      >
        <div
          className="flex items-center"
          data-testid="custom-report-results-left"
        >
          {!isSidebarOpen && (
            <div className="flex space-x-2 items-center">
              <div className="font-semibold">Dataset:</div>

              <DatasetConfigTags datasetConfig={datasetConfig} />

              {permissions.canConfigureCustomReports && (
                <Button
                  size="small"
                  className="flex items-center rounded-md bg-ciq-primary-900 hover:bg-ciq-primary-800 focus:bg-ciq-primary-800 active:bg-ciq-primary-800 cursor-pointer px-2 py-1"
                  onClick={() => setIsSidebarOpen(true)}
                  data-testid="custom-report-edit-dataset"
                >
                  <EditOutlined className="text-white hover:text-white font-medium" />
                </Button>
              )}
            </div>
          )}
        </div>
        <div
          className="flex space-x-2 items-center"
          data-testid="custom-report-results-right"
        >
          <div data-testid="custom-report-filter-chip">
            <FilterChipComponent
              key="CustomReport_FilterChip"
              gridRef={gridRef}
              ref={filterChipRef}
              onFilterCleared={saveFilterChange}
              setParentFilterItems={setGridFilterItems}
            />
          </div>

          {gridFilterItems.length > 0 && (
            <Divider
              orientation="vertical"
              className="border-black border-opacity-20 h-8"
            />
          )}

          {datasetConfig?.groupByField !== "none" &&
            !rowGroupExpandedState.isAllExpanded && (
              <Button
                onClick={onExpandAll}
                className="w-28"
                data-testid="custom-report-expand-all"
              >
                Expand All
              </Button>
            )}
          {datasetConfig?.groupByField !== "none" &&
            rowGroupExpandedState.isAllExpanded && (
              <Button
                onClick={onCollapseAll}
                className="w-28"
                data-testid="custom-report-collapse-all"
              >
                Collapse All
              </Button>
            )}

          {permissions.canConfigureCustomReports && (
            <Button
              onClick={() => {
                setIsOpenRenameColumnsModal(!isOpenRenameColumnsModal);
              }}
              data-testid="custom-report-rename-columns-button"
            >
              Rename Columns
            </Button>
          )}
          <div data-testid="custom-report-search">
            <CiqAgSearch
              key="ReportPage_search"
              gridRef={gridRef}
              placeholder="Search Report"
            />
          </div>
        </div>
      </div>
      <div
        className="custom-report-page-results-table flex-grow relative ag-theme-alpine overflow-auto bg-white "
        data-testid="custom-report-grid-container"
      >
        {datasetConfig && (
          <AgGridReact
            key={gridKey}
            ref={gridRef}
            rowData={rowData}
            getRowId={(p): string => {
              const d = p.data as
                | { id?: unknown; __rowInstanceKey?: string | number }
                | undefined;
              if (d?.__rowInstanceKey != null) {
                return String(d.__rowInstanceKey);
              }
              const id = d?.id;
              if (
                typeof id === "string" ||
                typeof id === "number" ||
                typeof id === "bigint"
              ) {
                return String(id);
              }
              return "custom-report-row";
            }}
            columnDefs={columnDefs}
            defaultColDef={conditionalFormattingDefaultColDef}
            getMainMenuItems={getConditionalFormattingHeaderMenuItems}
            excelStyles={mergedExcelStyles}
            autoGroupColumnDef={autoGroupColumnDef}
            overlayNoRowsTemplate="No Data Found"
            // headerHeight={36}
            rowHeight={gridStyleConfig.rowHeight}
            enableCellSpan={enableCellSpan}
            groupSuppressBlankHeader
            suppressContextMenu
            sideBar={sideBarConfig}
            loading={isGridLoading}
            rowClassRules={rowClassRules}
            context={{
              projectDetails,
              columnConfig,
              onCommentClick,
              isCommentsReadOnly: true,
              conditionalFormattingRules,
              onConditionalFormattingRulesChange,
              conditionalFormattingColumnConfig: {
                risk: {
                  filterType: "set",
                  options: [
                    RiskLevelType.High,
                    RiskLevelType.Medium,
                    RiskLevelType.Low
                  ]
                },
                milestone_state: {
                  filterType: "set",
                  options: statusMilestoneOptions
                },
                linked_submittals__status: {
                  filterType: "set",
                  options: Object.values(SubmittalStatusToStrMap)
                }
              }
            }}
            onGridReady={onGridReady}
            onGridColumnsChanged={onGridColumnsChanged}
            onFilterChanged={onFilterChanged}
            onColumnVisible={onColumnVisible}
            onColumnMoved={onColumnMoved}
            onDragStopped={(e: any) => {
              const classNames = [
                "ag-header-cell-resize",
                "ag-tool-panel-horizontal-resize"
              ];
              const targetClassName = `${e?.target?.className || ""}`;
              const isToolPanelDragEvent =
                targetClassName.includes("ag-column-select") ||
                targetClassName.includes("ag-tool-panel") ||
                targetClassName.includes("ag-column-drop");
              if (
                e?.api &&
                !classNames.includes(e?.target?.className) &&
                isFirstDataRendered
              ) {
                if (isToolPanelDragEvent) {
                  captureExpandedToolPanelGroups();
                  setTimeout(() => {
                    applySidebarTreeLayout();
                    scheduleRestoreExpandedToolPanelGroups();
                  }, 0);
                  return;
                }
                setTimeout(() => {
                  applySidebarTreeLayout();
                  scheduleRestoreExpandedToolPanelGroups();
                }, 0);
              }
            }}
            onFirstDataRendered={onFirstDataRendered}
            onSortChanged={onColumnSorted}
            maintainColumnOrder
            onFilterOpened={onGridFilterOpened}
            animateRows={false}
            onGridPreDestroyed={onGridPreDestroyed}
            suppressMoveWhenColumnDragging
            loadingOverlayComponent={GridLoadingIndicator}
            onExpandOrCollapseAll={onExpandOrCollapseAll}
            onRowGroupOpened={onRowGroupOpened}
            tooltipShowDelay={0}
          />
        )}
        {datasetConfig ? (
          <ConditionalFormattingToolPanel gridRef={gridRef} />
        ) : null}
      </div>
      {isOpenRenameColumnsModal && (
        <RenameColumnsModal
          isOpen={isOpenRenameColumnsModal}
          setIsOpen={setIsOpenRenameColumnsModal}
          onUpdateColumnConfig={async (con) => {
            captureExpandedToolPanelGroups();
            gridRef.current?.api.setGridOption("loading", true);
            await onUpdateColumnConfig(con);
            setTimeout(() => {
              gridRef.current?.api.refreshHeader();
              // refreshToolPanel rebuilds the panel from column defs, dropping
              // the date-block tree set via setColumnLayout.
              gridRef.current?.api.refreshToolPanel();
              applySidebarTreeLayout();
              scheduleRestoreExpandedToolPanelGroups();
              gridRef.current?.api.setGridOption("loading", false);
            }, 500);
          }}
        />
      )}
    </div>
  );

  return (
    <CollapsibleSidebarLayout
      sidebarState={[isCommentsSidebarOpen, () => {}]}
      sidebarWidth="420px"
      placement="right"
      sidebarHeader={
        <ErrorBoundary>
          <CommentsSidebarHeader
            title={commentsSidebarTitle}
            caption={commentsMaterial?.title || ""}
            onClose={closeCommentsSidebar}
          />
        </ErrorBoundary>
      }
      sidebarContent={
        commentsMaterial ? (
          <ErrorBoundary>
            <CommentsSidebarContent
              material={commentsMaterial}
              onCommentsCountChange={onCommentsCountChange}
            />
          </ErrorBoundary>
        ) : null
      }
      mainContent={resultsContent}
    />
  );
}

export default ResultsPanel;
